/**
 * Contenido (textos) del Test de Idoneidad Wio Capital V1.
 *
 * En runtime los textos vienen del EP `GET /suitability/questionnaire` (inyectados
 * en i18next por el wizard) y se leen con el hook `useWioCapitalContent`. Aquí solo
 * viven los tipos del blob, el accessor tipado y el helper `buildVfOptions`; el
 * fichero desaparecerá cuando el EP sea la única fuente y muera `questionnaire.ts`
 * (Fase 3).
 */
import { useTranslation } from 'react-i18next';
import { QUESTIONNAIRE_KEY_ROOT } from 'src/suitability/engine/i18n-questionnaire';

// ─── Tipos del contenido (forma del blob que devuelve el EP) ─────────────

export interface QuestionnaireOption {
  id: string;
  label: string;
  value: string;
}

export interface QuestionnaireRadioQuestion {
  prompt: string;
  options: QuestionnaireOption[];
}

export interface QuestionnaireP3SubQuestion {
  answerKey: string;
  prompt: string;
  options: QuestionnaireOption[];
}

export interface QuestionnaireP4Statement {
  id: string;
  answerKey: string;
  label: string;
}

export interface QuestionnaireP4Family {
  familyKey: string;
  familyLabel: string;
  statements: QuestionnaireP4Statement[];
}

export interface QuestionnaireP5Family {
  id: string;
  label: string;
  isComplex: boolean;
}

export interface QuestionnaireStepSection {
  key: string;
  title: string;
  intro: string;
}

export interface QuestionnaireStep {
  key: string;
  label: string;
  title?: string;
  intro?: string;
  sections?: QuestionnaireStepSection[];
}

export interface QuestionnaireVfLabels {
  true: string;
  false: string;
  unknown: string;
}

export interface QuestionnaireContent {
  steps: QuestionnaireStep[];
  questions: {
    age: QuestionnaireRadioQuestion & { answerKeys: string[] };
    p1: QuestionnaireRadioQuestion;
    p2: QuestionnaireRadioQuestion;
    p3: { questions: QuestionnaireP3SubQuestion[] };
    p4: { vfLabels: QuestionnaireVfLabels; families: QuestionnaireP4Family[] };
    p5: { complexBadge: string; families: QuestionnaireP5Family[] };
    p6: QuestionnaireRadioQuestion;
    p7: QuestionnaireRadioQuestion;
    p8: QuestionnaireRadioQuestion;
    p9: QuestionnaireRadioQuestion;
    p10: QuestionnaireRadioQuestion;
    p12: QuestionnaireRadioQuestion;
    p13: QuestionnaireRadioQuestion;
    p14: QuestionnaireRadioQuestion;
    p15: QuestionnaireRadioQuestion;
  };
}

// ─── Accessor: lee el contenido inyectado en i18n ────────────────────────

/** Getters tipados sobre el contenido del cuestionario inyectado en i18next. */
export const useWioCapitalContent = () => {
  const { t } = useTranslation();

  const read = <T>(key: string): T =>
    t(`${QUESTIONNAIRE_KEY_ROOT}.${key}`, { returnObjects: true }) as unknown as T;

  return {
    step: (key: string) => read<QuestionnaireStep>(`steps.${key}`),
    radio: (key: string) => read<QuestionnaireRadioQuestion>(`questions.${key}`),
    age: () =>
      read<QuestionnaireRadioQuestion & { answerKeys: string[] }>(
        'questions.age',
      ),
    p3: () => read<{ questions: QuestionnaireP3SubQuestion[] }>('questions.p3'),
    p4: () =>
      read<{ vfLabels: QuestionnaireVfLabels; families: QuestionnaireP4Family[] }>(
        'questions.p4',
      ),
    p5: () =>
      read<{ complexBadge: string; families: QuestionnaireP5Family[] }>(
        'questions.p5',
      ),
  };
};

/** Construye las 3 opciones V/F de una afirmación P4 a partir de sus etiquetas. */
export const buildVfOptions = (
  statementId: string,
  vfLabels: QuestionnaireVfLabels,
): QuestionnaireOption[] => [
  { id: `${statementId}a`, label: vfLabels.true, value: `${statementId}a` },
  { id: `${statementId}b`, label: vfLabels.false, value: `${statementId}b` },
  { id: `${statementId}c`, label: vfLabels.unknown, value: `${statementId}c` },
];
